import { UI, HStack, Stack, VStack } from '@genial/components';
import { BoxRadius, Spacing, Typography } from '@genial/design-tokens';
import React from 'react';

import { InputMoney } from '../../../../../../../../../components/InputMoney';
import { TableContent } from '../../../../../../../../../components/TableContent';
import { useGlobalAssetContext } from '../../../../../contexts';
import { useFundsSellAssetController } from '../../../../../hooks/products/FundsSell';
import { useFundsSellTaxSimulationController } from '../../../../../hooks/products/FundsSell/taxSimulation';
import { LateralBar } from '../../../components/LateralBar';
import { ListLoadingItemMobile } from '../ListLoadingItem/mobile';

import { FundsListItemTypes } from '.';

export const FundsListItemMobile: React.FC<FundsListItemTypes.Props> = ({ offer }) => {
  const id = offer.productIdFormatted;
  const {
    handleInputFocus,
    handleInputClear,
    isFocused,
    handleInputBlur,
    handleSubmit,
    control,
    hasError,
    helpText,
    currentValue,
    currentValueFormatted,
    isTotalRedemption,
    handleToggleIsTotalRedemption,
    isTotalRedemptionChecked,
  } = useFundsSellAssetController(offer);
  const { infoGetter, selectedTab } = useGlobalAssetContext();

  const completeOffer = { ...offer, orderValue: currentValue, isTotalRedemption };

  const { isLoadingTaxes, taxSimulationResponse } = useFundsSellTaxSimulationController({
    valueToSimulate: currentValue,
    product: completeOffer,
  });

  const { Button, isSelected, forceLoading } = infoGetter(
    'fundsSell',
    completeOffer,
    'productIdNormalized'
  );

  const middleInfos1 = [
    {
      label: 'Resgate total',
      value: (
        <UI.Switch onToggle={handleToggleIsTotalRedemption} isChecked={isTotalRedemptionChecked} />
      ),
    },
    {
      label: 'IR estimado',
      value: (
        <UI.Text color="neutral.dark" isLoading={isLoadingTaxes}>
          {taxSimulationResponse?.irValueNormalized || offer.estimatedIncomeTax}
        </UI.Text>
      ),
    },
  ];

  const middleInfos2 = [
    {
      label: 'Valor líquido estimado',
      value: (
        <UI.Text color="neutral.dark" isLoading={isLoadingTaxes}>
          {taxSimulationResponse?.liquidityValueNormalized || offer.estimatedNetValue}
        </UI.Text>
      ),
    },
    {
      label: 'Cotização',
      value: <UI.Text color="neutral.dark">{offer.quotationDateFormatted}</UI.Text>,
    },
  ];

  const middleInfos3 = [
    {
      label: 'Liquidação',
      value: <UI.Text color="neutral.dark">{offer.liquidationDateFormatted}</UI.Text>,
    },
  ];

  if (forceLoading) {
    return <ListLoadingItemMobile />;
  }

  return (
    <TableContent
      direction="row"
      space={0}
      p={0}
      px={Spacing['inline.xxxs']}
      isHighlighted={isSelected}>
      <LateralBar
        backgroundColor={selectedTab.color}
        borderTopLeftRadius={BoxRadius.sm}
        borderBottomLeftRadius={BoxRadius.sm}
      />
      <VStack padding={Spacing['inset.sm']} space={Spacing['stack.xxxs']} w="full">
        <HStack justifyContent="space-between" alignItems="center">
          <UI.Text variant="semibold" color="neutral.black" fontSize={Typography['fontsize.lg']}>
            {offer.productName}
          </UI.Text>
        </HStack>
        <HStack justifyContent="space-between">
          {middleInfos1.map(({ label, value }) => (
            <VStack space={Spacing['stack.quarck']} key={label} w="48%">
              <UI.Text color="neutral.black" variant="semibold">
                {label}
              </UI.Text>
              {value}
            </VStack>
          ))}
        </HStack>
        <HStack justifyContent="space-between">
          {middleInfos2.map(({ label, value }) => (
            <VStack space={Spacing['stack.quarck']} key={label} w="48%">
              <UI.Text color="neutral.black" variant="semibold">
                {label}
              </UI.Text>
              {value}
            </VStack>
          ))}
        </HStack>
        <HStack justifyContent="space-between">
          {middleInfos3.map(({ label, value }) => (
            <VStack space={Spacing['stack.quarck']} key={label} w="48%">
              <UI.Text color="neutral.black" variant="semibold">
                {label}
              </UI.Text>
              {value}
            </VStack>
          ))}
        </HStack>
        <HStack space={Spacing['inline.xxxs']} justifyContent="space-between" w="full">
          <VStack space={Spacing['stack.quarck']} flexGrow={1}>
            <UI.Text color="neutral.black" variant="semibold">
              Valor a resgatar
            </UI.Text>
            <InputMoney
              id={`funds-sell-input${id}`}
              name="redemptionValue"
              control={control}
              handleFocus={handleInputFocus}
              handleBlur={handleInputBlur}
              handleSubmit={handleSubmit}
              handleClear={handleInputClear}
              currentValue={currentValueFormatted}
              hasFocus={isFocused}
              minValue={offer.minimumTransaction || 0}
              isDisabled={isTotalRedemptionChecked}
              customInputProps={{
                ...{ ...(isFocused && hasError && { error: hasError }) },
                ...{ ...(isFocused && !hasError && { helpText }) },
              }}
            />
          </VStack>
          <Stack alignSelf="flex-end">
            <Button
              id={selectedTab.typeGetterId}
              offer={completeOffer}
              isDisabled={typeof hasError?.message === 'string'}
              productContextConstant={selectedTab}
            />
          </Stack>
        </HStack>
      </VStack>
    </TableContent>
  );
};
